<script setup lang="ts">
  import gsap from 'gsap'
  import { SplitText } from 'gsap/SplitText'

  const root = ref<HTMLElement>()
  let media: gsap.MatchMedia | undefined

  onMounted(() => {
    gsap.registerPlugin(SplitText)
    media = gsap.matchMedia()

    media.add('(prefers-reduced-motion: no-preference)', () => {
      const q = gsap.utils.selector(root.value)
      const title = SplitText.create(q('.contact-title')[0], { type: 'words,chars', mask: 'chars' })

      gsap.timeline({ defaults: { ease: 'expo.out', lazy: false } })
        .from(title.chars, { yPercent: 110, duration: 1.1, stagger: 0.03 }, 0)
        .from(q('.contact-rule'), { scaleX: 0, duration: 1.2, ease: 'power3.inOut' }, 0.3)
        .from(q('.contact-side > *'), { y: 20, opacity: 0, duration: 0.7, stagger: 0.09, ease: 'power3.out', clearProps: 'all' }, 0.5)
        .from(q('.contact-field'), { y: 20, opacity: 0, duration: 0.6, stagger: 0.07, ease: 'power3.out', clearProps: 'all' }, 0.6)

      return () => title.revert()
    }, root.value)
  })

  onBeforeUnmount(() => media?.revert())

  const { profile } = useAppConfig()
  const { t } = useI18n()

  const loading = ref(false)
  const form = ref({
    email: '',
    message: '',
    phone: '',
    fullname: '',
    subject: '',
  })

  async function onSubmit() {
    loading.value = true
    try {
      await $fetch('/api/emails/send', {
        method: 'POST',
        body: form.value,
      })
      toast.success(t('contact.success'), {
        description: t('contact.success_description'),
      })
      form.value = {
        email: '',
        message: '',
        phone: '',
        fullname: '',
        subject: '',
      }
    }
    catch (error) {
      console.error(error)
      toast.error(t('contact.error'), {
        description: t('contact.error_description'),
      })
    }
    finally {
      loading.value = false
    }
  }
</script>

<template>
  <section
    ref="root"
    class="mx-auto max-w-7xl overflow-x-clip px-4 pb-24 sm:px-6 lg:px-8"
  >
    <header class="pt-10 sm:pt-24">
      <h1 class="contact-title whitespace-nowrap text-[clamp(3rem,11.5vw,10rem)] font-semibold leading-[0.95] tracking-tighter text-white">
        <slot
          name="title"
          mdc-unwrap="p"
        />
      </h1>
      <div class="contact-rule mt-6 h-px origin-left bg-white/20" />
    </header>

    <div class="mt-10 grid items-start gap-14 lg:mt-16 lg:grid-cols-12 lg:gap-16">
      <!-- direct ways to reach me -->
      <div class="contact-side lg:sticky lg:top-24 lg:col-span-5">
        <p class="max-w-md text-pretty text-xl leading-relaxed text-white/60">
          <slot
            name="subtitle"
            mdc-unwrap="p"
          />
        </p>
        <a
          :href="`mailto:${profile?.email}`"
          class="contact-link group mt-10 block"
        >
          <span class="text-sm text-white/50">{{ $t('contact.label_email') }}</span>
          <span class="mt-1 block break-all text-2xl font-semibold tracking-tight text-white sm:text-3xl">{{ profile?.email }}</span>
        </a>
        <a
          :href="`tel:${profile?.phone?.replace(/\s/g, '')}`"
          class="contact-link group mt-7 block"
        >
          <span class="text-sm text-white/50">{{ $t('contact.label_phone') }}</span>
          <span class="mt-1 block text-2xl font-semibold tracking-tight text-white sm:text-3xl">{{ profile?.phone }}</span>
        </a>
        <p class="mt-7 text-white/60">
          {{ $t('agency.locations') }}
        </p>
        <HomeSocial class="!mb-0 !mt-5 !justify-start" />
      </div>

      <!-- form -->
      <form
        class="grid gap-x-8 gap-y-7 sm:grid-cols-2 lg:col-span-7"
        @submit.prevent="onSubmit"
      >
        <UFormField
          :label="$t('contact.label_name')"
          name="fullname"
          required
          class="contact-field"
        >
          <UInput
            v-model="form.fullname"
            type="text"
            autocomplete="name"
            :placeholder="$t('contact.placeholders.name')"
            size="xl"
            variant="none"
            class="contact-input w-full"
            required
          />
        </UFormField>

        <UFormField
          :label="$t('contact.label_email')"
          name="email"
          required
          class="contact-field"
        >
          <UInput
            v-model="form.email"
            type="email"
            autocomplete="email"
            :placeholder="$t('contact.placeholders.email')"
            size="xl"
            variant="none"
            class="contact-input w-full"
            required
          />
        </UFormField>

        <UFormField
          :label="$t('contact.label_phone')"
          name="phone"
          class="contact-field"
        >
          <UInput
            v-model="form.phone"
            autocomplete="tel"
            :placeholder="$t('contact.placeholders.phone')"
            size="xl"
            variant="none"
            class="contact-input w-full"
          />
        </UFormField>

        <UFormField
          :label="$t('contact.label_subject')"
          name="subject"
          required
          class="contact-field"
        >
          <UInput
            v-model="form.subject"
            :placeholder="$t('contact.label_subject')"
            size="xl"
            variant="none"
            class="contact-input w-full"
            required
          />
        </UFormField>

        <UFormField
          :label="$t('contact.label_message')"
          name="message"
          required
          class="contact-field sm:col-span-2"
        >
          <UTextarea
            v-model="form.message"
            autoresize
            :rows="4"
            :placeholder="$t('contact.placeholders.message')"
            size="xl"
            variant="none"
            class="contact-input w-full"
            required
          />
        </UFormField>

        <div class="contact-field sm:col-span-2">
          <button
            type="submit"
            :disabled="loading"
            class="group flex h-14 cursor-pointer items-center gap-4 rounded-full bg-white pl-7 pr-2 font-medium text-neutral-950 transition-opacity disabled:opacity-60"
          >
            {{ $t("contact.submit") }}
            <span class="flex size-10 items-center justify-center rounded-full bg-neutral-950 text-white">
              <UIcon
                :name="loading ? 'lucide:loader' : 'heroicons:arrow-right'"
                class="size-4 transition-transform duration-300 group-hover:translate-x-0.5"
                :class="loading ? 'animate-spin' : ''"
              />
            </span>
          </button>
        </div>
      </form>
    </div>
  </section>
</template>

<style>
  /* Fields without a box: a single line that lights up on focus. */
  .contact-input {
    border-bottom: 1px solid rgb(255 255 255 / 0.2);
    transition: border-color 200ms;
  }

  .contact-input:focus-within {
    border-color: white;
  }

  .contact-input input,
  .contact-input textarea {
    padding-inline: 0;
  }
</style>
